import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, CalendarClock, ClipboardCheck, ExternalLink, FileText, MapPin, Pencil, QrCode,
  Phone, Mail, Plus, RefreshCw, Trash2, Wrench,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PageHeader from "@/app/PageHeader";
import { useSession } from "@/app/session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIT_TYPES = [
  ["restock", "Restock"], ["clean", "Clean"], ["repair", "Repair"],
  ["install", "Install"], ["removal", "Removal"], ["other", "Other"],
];
const STATUSES = [["active", "Active"], ["paused", "Paused"], ["prospect", "Signed, not installed"], ["ended", "Ended"]];

const today = () => new Date().toLocaleDateString("en-CA");
const fmtDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

const inputCls = "border-white/10 bg-vortex-ink/60 text-slate-100 focus-visible:ring-vortex-cyan";
const selectCls = "h-10 w-full rounded-md border border-white/10 bg-vortex-ink/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-vortex-cyan";
const labelCls = "font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase";
const primaryBtn = "flex items-center justify-center gap-2 rounded-full bg-vortex-cyan px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase text-vortex-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(92,179,226,0.4)] disabled:opacity-50";
const ghostBtn = "flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-300 transition-colors hover:border-vortex-cyan/60 hover:text-vortex-cyan";
const iconBtn = "text-slate-500 transition-colors hover:text-red-400";
const card = "border border-white/10 bg-vortex-surface/60";

const Field = ({ id, label, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className={labelCls}>{label}</Label>
    {children}
  </div>
);

const SectionTitle = ({ kicker, title, right }) => (
  <div className="flex flex-wrap items-end justify-between gap-4">
    <div>
      <p className="font-mono text-xs tracking-[0.25em] text-vortex-cyan uppercase">{kicker}</p>
      <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white">{title}</h2>
    </div>
    {right}
  </div>
);

const Pill = ({ tone = "cyan", children }) => {
  const tones = {
    cyan: "border-vortex-cyan/40 text-vortex-cyan",
    amber: "border-amber-400/50 text-amber-300",
    red: "border-red-400/50 text-red-300",
    slate: "border-white/15 text-slate-400",
  };
  return <span className={`rounded-full border px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase ${tones[tone]}`}>{children}</span>;
};

const contractTone = (state) => ({ expiring: "red", expired: "slate", renewing: "amber" }[state] || "cyan");
const contractLabel = (state) => ({ expiring: "Ends soon", expired: "Expired", renewing: "Auto-renews soon", active: "Active" }[state] || state);

const emptyAccount = { name: "", contact_name: "", email: "", phone: "", address: "", status: "active", notes: "" };
const emptyMachine = { name: "", machine_type: "Smart cooler", placement: "", location_id: "", service_every_days: 7, planner_url: "", notes: "" };
const emptyContract = { title: "Placement agreement", start_date: "", term_months: 12, auto_renew: true, notice_days: 30, commission_pct: "", commission_basis: "gross", document_url: "", notes: "" };

const AdminService = () => {
  const { token, onAuthError } = useSession();
  const [overview, setOverview] = useState(null);
  const [accounts, setAccounts] = useState([]);
  // ?account=<id> (from workspace search or the home page) opens that account.
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("account");
  const setSelectedId = useCallback((id) => setParams(id ? { account: id } : {}), [setParams]);
  const [detail, setDetail] = useState(null);
  const [newAccount, setNewAccount] = useState(emptyAccount);
  const [busy, setBusy] = useState(false);
  const [visitFor, setVisitFor] = useState(null);
  const [visit, setVisit] = useState({});

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fail = onAuthError;

  const loadAll = useCallback(() => {
    axios.get(`${API}/service/overview`, auth()).then((r) => setOverview(r.data)).catch((e) => fail(e, "Could not load today's route."));
    axios.get(`${API}/service/accounts`, auth()).then((r) => setAccounts(r.data)).catch((e) => fail(e, "Could not load accounts."));
  }, [auth, fail]);

  const loadDetail = useCallback((id) => {
    axios.get(`${API}/service/accounts/${id}`, auth()).then((r) => setDetail(r.data)).catch((e) => fail(e, "Could not load account."));
  }, [auth, fail]);

  useEffect(() => { if (token) loadAll(); }, [token, loadAll]);
  useEffect(() => { if (token && selectedId) loadDetail(selectedId); else setDetail(null); }, [token, selectedId, loadDetail]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [selectedId]);

  const refresh = () => { loadAll(); if (selectedId) loadDetail(selectedId); };

  const call = async (fn, ok, fallback) => {
    setBusy(true);
    try {
      const res = await fn();
      if (ok) toast.success(ok);
      refresh();
      return res?.data ?? true;
    } catch (err) {
      fail(err, fallback);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const openVisit = (m) => {
    setVisitFor(m);
    setVisit({ visit_date: today(), visit_type: "restock", units_stocked: "", cash_collected: "", notes: "" });
  };

  const saveVisit = async (e) => {
    e.preventDefault();
    const body = {
      machine_id: visitFor.id,
      visit_date: visit.visit_date,
      visit_type: visit.visit_type,
      units_stocked: visit.units_stocked === "" ? null : Number(visit.units_stocked),
      cash_collected: visit.cash_collected === "" ? null : Number(visit.cash_collected),
      notes: visit.notes,
    };
    if (await call(() => axios.post(`${API}/service/visits`, body, auth()), `Visit logged for ${visitFor.name}.`, "Could not log visit.")) {
      setVisitFor(null);
    }
  };

  return (
    <div data-testid="service-page">
        <PageHeader title="Accounts & Service" testId="service-header"
          intro="Your accounts, the machines at each one, every stocking visit and the contract behind it."
          actions={<button onClick={refresh} className={ghostBtn} data-testid="service-refresh"><RefreshCw size={14} /> Refresh</button>} />

        {selectedId ? (
          <AccountDetail
            detail={detail}
            busy={busy}
            onBack={() => setSelectedId(null)}
            onLogVisit={openVisit}
            api={{ call, auth }}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <>
            <Today overview={overview} onLogVisit={openVisit} onOpenAccount={setSelectedId} />

            <div className="mt-20" data-testid="service-accounts">
              <SectionTitle kicker="Accounts" title="Customers" right={
                <p className="font-mono text-[10px] tracking-[0.2em] text-slate-600 uppercase">{accounts.length} account{accounts.length === 1 ? "" : "s"}</p>
              } />
              <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12">
                <form
                  className={`p-8 lg:col-span-4 ${card}`}
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const acc = await call(() => axios.post(`${API}/service/accounts`, newAccount, auth()), `${newAccount.name} added.`, "Could not add account.");
                    if (acc) { setNewAccount(emptyAccount); setSelectedId(acc.id); }
                  }}
                  data-testid="service-add-account"
                >
                  <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Add account</p>
                  <div className="mt-6 space-y-4">
                    <Field id="na-name" label="Business name">
                      <Input id="na-name" required value={newAccount.name} onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })} className={inputCls} placeholder="e.g. Brandywine Medical Group" />
                    </Field>
                    <Field id="na-contact" label="Contact">
                      <Input id="na-contact" value={newAccount.contact_name} onChange={(e) => setNewAccount({ ...newAccount, contact_name: e.target.value })} className={inputCls} placeholder="Office or property manager" />
                    </Field>
                    <Field id="na-address" label="Address">
                      <Input id="na-address" value={newAccount.address} onChange={(e) => setNewAccount({ ...newAccount, address: e.target.value })} className={inputCls} />
                    </Field>
                  </div>
                  <button type="submit" disabled={busy} className={`mt-8 w-full ${primaryBtn}`}><Plus size={15} /> Add account</button>
                </form>

                <div className="border border-white/10 lg:col-span-8">
                  {accounts.length === 0 ? (
                    <p className="px-6 py-10 text-sm text-slate-500">No accounts yet. Add your first customer on the left.</p>
                  ) : accounts.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => setSelectedId(a.id)}
                      className="grid w-full grid-cols-12 items-center gap-4 border-b border-white/5 px-6 py-5 text-left transition-colors duration-300 hover:bg-vortex-surface/40"
                      data-testid={`service-account-${a.id}`}
                    >
                      <span className="col-span-12 flex items-center gap-3 sm:col-span-5">
                        <Building2 size={15} className="shrink-0 text-vortex-cyan" />
                        <span className="text-sm font-medium text-slate-100">{a.name}</span>
                      </span>
                      <span className="col-span-6 text-sm text-slate-400 sm:col-span-3">
                        {a.machine_count} machine{a.machine_count === 1 ? "" : "s"}
                        {a.machines_due > 0 && <span className="text-amber-300"> · {a.machines_due} due</span>}
                      </span>
                      <span className="col-span-6 flex justify-end gap-2 sm:col-span-4">
                        {a.status !== "active" && <Pill tone="slate">{a.status}</Pill>}
                        {a.contract_state
                          ? <Pill tone={contractTone(a.contract_state)}>{a.contract_state === "expired" ? "Contract expired" : `Term ends ${fmtDate(a.contract_end)}`}</Pill>
                          : <Pill tone="red">No contract</Pill>}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        <Dialog open={!!visitFor} onOpenChange={(o) => !o && setVisitFor(null)}>
          <DialogContent className="border-white/10 bg-vortex-surface text-slate-100 sm:max-w-md" data-testid="visit-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold tracking-tight text-white">Log a visit</DialogTitle>
              <DialogDescription className="text-sm text-slate-400">
                {visitFor?.name}{visitFor?.account_name ? ` at ${visitFor.account_name}` : ""}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={saveVisit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field id="v-date" label="Date">
                  <Input id="v-date" type="date" required max={today()} value={visit.visit_date || ""} onChange={(e) => setVisit({ ...visit, visit_date: e.target.value })} className={inputCls} />
                </Field>
                <Field id="v-type" label="Type">
                  <select id="v-type" value={visit.visit_type} onChange={(e) => setVisit({ ...visit, visit_type: e.target.value })} className={selectCls}>
                    {VISIT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field id="v-units" label="Units stocked">
                  <Input id="v-units" type="number" min="0" inputMode="numeric" value={visit.units_stocked} onChange={(e) => setVisit({ ...visit, units_stocked: e.target.value })} className={inputCls} />
                </Field>
                <Field id="v-cash" label="Cash collected">
                  <Input id="v-cash" type="number" min="0" step="0.01" inputMode="decimal" value={visit.cash_collected} onChange={(e) => setVisit({ ...visit, cash_collected: e.target.value })} className={inputCls} />
                </Field>
              </div>
              <Field id="v-notes" label="Notes">
                <Textarea id="v-notes" rows={3} value={visit.notes} onChange={(e) => setVisit({ ...visit, notes: e.target.value })} className={inputCls} placeholder="Sold out of cold brew, wiped glass, card reader OK…" />
              </Field>
              <button type="submit" disabled={busy} className={`w-full ${primaryBtn}`}><ClipboardCheck size={15} /> Save visit</button>
            </form>
          </DialogContent>
        </Dialog>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className={`p-6 ${card}`}>
    <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">{label}</p>
    <p className="mt-3 font-heading text-3xl font-black tracking-tight text-white">{value}</p>
  </div>
);

const Today = ({ overview, onLogVisit, onOpenAccount }) => {
  if (!overview) return <p className="mt-14 text-sm text-slate-500">Loading today's route…</p>;
  const { due, upcoming, renewals, stats } = overview;
  return (
    <div className="mt-14" data-testid="service-today">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Active accounts" value={stats.active_accounts} />
        <Stat label="Machines" value={stats.machines} />
        <Stat label="Visits this month" value={stats.visits_this_month} />
        <Stat label="Cash this month" value={money(stats.cash_this_month)} />
      </div>

      <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionTitle kicker="Route" title="Due for service" />
          <div className="mt-6 border border-white/10">
            {due.length === 0 ? (
              <p className="px-6 py-10 text-sm text-slate-500">Nothing due today.</p>
            ) : due.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-4 border-b border-white/5 px-6 py-5" data-testid={`due-${m.id}`}>
                <div className="min-w-0 flex-1">
                  <button onClick={() => onOpenAccount(m.account_id)} className="text-left text-sm font-medium text-slate-100 hover:text-vortex-cyan">
                    {m.account_name} <span className="text-slate-500">· {m.location_name && m.location_name !== m.account_name ? `${m.location_name} · ` : ""}{m.name}</span>
                  </button>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                    <MapPin size={12} className="text-vortex-cyan" />{m.address || m.placement || "No address"}
                  </p>
                </div>
                {m.never_serviced ? <Pill tone="amber">First visit</Pill>
                  : m.days_overdue > 0 ? <Pill tone="red">{m.days_overdue}d overdue</Pill>
                  : <Pill>Due today</Pill>}
                <button onClick={() => onLogVisit(m)} className={ghostBtn}><ClipboardCheck size={13} /> Log visit</button>
              </div>
            ))}
          </div>
          {upcoming.length > 0 && (
            <>
              <p className="mt-8 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Next 7 days</p>
              <div className="mt-3 border border-white/10">
                {upcoming.map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-4 border-b border-white/5 px-6 py-4 text-sm">
                    <span className="text-slate-300">{m.account_name} <span className="text-slate-500">· {m.location_name && m.location_name !== m.account_name ? `${m.location_name} · ` : ""}{m.name}</span></span>
                    <span className="font-mono text-xs text-slate-500">{fmtDate(m.next_due)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="lg:col-span-5">
          <SectionTitle kicker="Contracts" title="Coming up for renewal" />
          <div className="mt-6 border border-white/10">
            {renewals.length === 0 ? (
              <p className="px-6 py-10 text-sm text-slate-500">No contracts end in the next 90 days.</p>
            ) : renewals.map((c) => (
              <button key={c.id} onClick={() => onOpenAccount(c.account_id)} className="block w-full border-b border-white/5 px-6 py-5 text-left hover:bg-vortex-surface/40">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-medium text-slate-100">{c.account_name}</span>
                  <Pill tone={contractTone(c.state)}>{contractLabel(c.state)}</Pill>
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  Term ends {fmtDate(c.term_end)} · notice by {fmtDate(c.notice_by)}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AccountDetail = ({ detail, busy, onBack, onLogVisit, api, onDeleted }) => {
  const { call, auth } = api;
  const [edit, setEdit] = useState(null);
  const [machine, setMachine] = useState(emptyMachine);
  const [contract, setContract] = useState(emptyContract);
  const [newLoc, setNewLoc] = useState("");
  const [renaming, setRenaming] = useState(null);
  const machineFormRef = useRef(null);

  useEffect(() => {
    if (detail) {
      setEdit({
        name: detail.name, contact_name: detail.contact_name || "", email: detail.email || "", phone: detail.phone || "",
        address: detail.address || "", status: detail.status, notes: detail.notes || "",
      });
    }
  }, [detail]);

  if (!detail || !edit) return <p className="mt-14 text-sm text-slate-500">Loading account…</p>;

  // Every machine sits at one of the account's locations; machines from before that rule show up
  // under "Not placed" until they're given one.
  const myLocations = detail.locations || [];
  const placed = new Set(myLocations.map((l) => l.id));
  const groups = myLocations.map((loc) => ({ loc, machines: detail.machines.filter((m) => m.location_id === loc.id) }));
  const unplaced = detail.machines.filter((m) => !placed.has(m.location_id));
  if (unplaced.length) groups.push({ loc: null, machines: unplaced });
  const startMachine = (locationId) => {
    setMachine({ ...emptyMachine, location_id: locationId });
    machineFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => document.getElementById("m-name")?.focus(), 300);
  };

  return (
    <div className="mt-14" data-testid="service-account-detail">
      <button onClick={onBack} className={ghostBtn}><ArrowLeft size={13} /> All accounts</button>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <h2 className="font-heading text-3xl font-black tracking-tighter text-white">{detail.name}</h2>
        {detail.phone && <a href={`tel:${detail.phone}`} className="flex items-center gap-1.5 text-sm text-vortex-cyan"><Phone size={13} />{detail.phone}</a>}
        {detail.email && <a href={`mailto:${detail.email}`} className="flex items-center gap-1.5 text-sm text-vortex-cyan"><Mail size={13} />{detail.email}</a>}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <form
          className={`space-y-4 p-8 lg:col-span-4 ${card}`}
          onSubmit={(e) => { e.preventDefault(); call(() => axios.put(`${API}/service/accounts/${detail.id}`, edit, auth()), "Account saved.", "Could not save account."); }}
        >
          <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Account</p>
          <Field id="a-name" label="Business name"><Input id="a-name" required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={inputCls} /></Field>
          <Field id="a-status" label="Status">
            <select id="a-status" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={selectCls}>
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field id="a-contact" label="Contact"><Input id="a-contact" value={edit.contact_name} onChange={(e) => setEdit({ ...edit, contact_name: e.target.value })} className={inputCls} /></Field>
          <Field id="a-phone" label="Phone"><Input id="a-phone" type="tel" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} className={inputCls} /></Field>
          <Field id="a-email" label="Email"><Input id="a-email" type="email" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} className={inputCls} /></Field>
          <Field id="a-address" label="Address"><Input id="a-address" value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} className={inputCls} /></Field>
          <Field id="a-notes" label="Notes"><Textarea id="a-notes" rows={3} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} className={inputCls} placeholder="Access hours, loading dock, who has the key…" /></Field>
          <button type="submit" disabled={busy} className={`w-full ${primaryBtn}`}>Save account</button>
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Delete ${detail.name} and its contracts? This can't be undone.`)) return;
              if (await call(() => axios.delete(`${API}/service/accounts/${detail.id}`, auth()), "Account deleted.", "Could not delete account.")) onDeleted();
            }}
            className="w-full pt-2 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase hover:text-red-400"
          >
            Delete account
          </button>
        </form>

        <div className="space-y-14 lg:col-span-8">
          {/* Locations, each with the machines placed there */}
          <div data-testid="account-locations">
            <SectionTitle kicker="Locations & machines" title="Where the equipment is" />
            <div className="mt-6 space-y-4">
              {groups.map(({ loc, machines }) => (
                <div key={loc ? loc.id : "none"} className="border border-white/10" data-testid={loc ? `account-location-${loc.id}` : "account-location-none"}>
                  <div className="flex flex-wrap items-center gap-3 border-b border-white/10 bg-vortex-surface/60 px-6 py-4">
                    <MapPin size={15} className={loc ? "text-vortex-cyan" : "text-amber-300"} />
                    {loc && renaming?.id === loc.id ? (
                      <form className="flex flex-1 gap-2" onSubmit={async (e) => {
                        e.preventDefault();
                        const body = { name: renaming.name, address: loc.address || "", account_id: detail.id };
                        if (await call(() => axios.put(`${API}/locations/${loc.id}`, body, auth()), "Location renamed.", "Could not rename location.")) setRenaming(null);
                      }}>
                        <Input aria-label="Location name" autoFocus required value={renaming.name}
                          onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} className={`h-9 ${inputCls}`} />
                        <button type="submit" disabled={busy} className={ghostBtn}>Save</button>
                        <button type="button" onClick={() => setRenaming(null)} className="px-2 text-xs text-slate-500 hover:text-slate-300">Cancel</button>
                      </form>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-white">{loc ? loc.name : "Not placed at a location yet"}</span>
                        {loc?.address && <span className="text-xs text-slate-500">{loc.address}</span>}
                        <span className="text-xs text-slate-500">· {machines.length} machine{machines.length === 1 ? "" : "s"}</span>
                      </>
                    )}
                    {loc && renaming?.id !== loc.id && (
                      <span className="ml-auto flex items-center gap-2">
                        <button onClick={() => setRenaming({ id: loc.id, name: loc.name })} aria-label={`Rename ${loc.name}`}
                          className="rounded-md p-1.5 text-slate-500 hover:text-vortex-cyan"><Pencil size={14} /></button>
                        <Link to={`/admin/locations?qr=${loc.id}`} className={ghostBtn}><QrCode size={13} /> QR card</Link>
                        <button onClick={() => startMachine(loc.id)} className={ghostBtn} data-testid={`add-machine-${loc.id}`}><Plus size={13} /> Machine</button>
                      </span>
                    )}
                  </div>
                  {machines.length === 0 && <p className="px-6 py-5 text-sm text-slate-500">No machines here yet.</p>}
                  {machines.map((m) => (
                    <div key={m.id} className="border-b border-white/5 px-6 py-5 last:border-b-0" data-testid={`machine-${m.id}`}>
                      <div className="flex flex-wrap items-center gap-3">
                        <Wrench size={14} className="text-vortex-cyan" />
                        <span className="text-sm font-medium text-slate-100">{m.name}</span>
                        <span className="text-xs text-slate-500">{[m.machine_type, m.placement].filter(Boolean).join(" · ")}</span>
                        <span className="ml-auto flex items-center gap-3">
                          {m.planner_machine_id ? (
                            <Link to={`/admin/planner/machines/${m.planner_machine_id}`} className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-vortex-cyan">
                              Planogram
                            </Link>
                          ) : (
                            <Link to="/admin/planner/machines" className="font-mono text-[10px] tracking-[0.15em] uppercase text-slate-500 hover:text-vortex-cyan">
                              Set up planogram
                            </Link>
                          )}
                          {!m.planner_machine_id && m.planner_url && (
                            <a href={m.planner_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-vortex-cyan">
                              Planogram <ExternalLink size={11} />
                            </a>
                          )}
                          <button onClick={() => onLogVisit({ ...m, account_name: detail.name })} className={ghostBtn}><ClipboardCheck size={13} /> Log visit</button>
                          <button
                            aria-label={`Remove ${m.name}`}
                            className={iconBtn}
                            onClick={() => window.confirm(`Remove ${m.name} and its visit history?`)
                              && call(() => axios.delete(`${API}/service/machines/${m.id}`, auth()), `${m.name} removed.`, "Could not remove machine.")}
                          >
                            <Trash2 size={15} />
                          </button>
                        </span>
                      </div>
                      <p className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-slate-400">
                        <span><CalendarClock size={12} className="mr-1 inline text-vortex-cyan" />Every {m.service_every_days} days</span>
                        <span>Last visit {fmtDate(m.last_visit)}</span>
                        <span className={m.days_overdue > 0 ? "text-red-300" : ""}>Next due {fmtDate(m.next_due)}</span>
                        {(myLocations.length > 1 || !loc) && (
                          <label className="flex items-center gap-2">
                            {loc ? "Move to" : "Place at"}
                            <select aria-label={`Location for ${m.name}`} value={loc ? loc.id : ""}
                              onChange={(e) => e.target.value && call(() => axios.put(`${API}/service/machines/${m.id}/location`, { location_id: e.target.value }, auth()), `${m.name} moved.`, "Could not move machine.")}
                              className="h-8 rounded-md border border-white/10 bg-vortex-ink/60 px-2 text-xs text-slate-100">
                              {!loc && <option value="">Pick a location</option>}
                              {myLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                          </label>
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <form
              className={`mt-4 flex flex-col gap-3 p-4 sm:flex-row ${card}`}
              onSubmit={async (e) => {
                e.preventDefault();
                const body = { name: newLoc, address: detail.address || "", account_id: detail.id };
                if (await call(() => axios.post(`${API}/locations`, body, auth()), `${newLoc} added.`, "Could not add location.")) setNewLoc("");
              }}
            >
              <Input aria-label="New location name" required value={newLoc} onChange={(e) => setNewLoc(e.target.value)}
                className={inputCls} placeholder={`New location, e.g. ${detail.name}: warehouse`} />
              <button type="submit" disabled={busy} className={`shrink-0 ${ghostBtn}`}><Plus size={13} /> Add location</button>
            </form>

            <form
              ref={machineFormRef}
              className={`mt-4 grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 ${card}`}
              data-testid="add-machine-form"
              onSubmit={async (e) => {
                e.preventDefault();
                const body = { ...machine, service_every_days: Number(machine.service_every_days) || 7 };
                if (await call(() => axios.post(`${API}/service/accounts/${detail.id}/machines`, body, auth()), `${machine.name} added.`, "Could not add machine.")) setMachine(emptyMachine);
              }}
            >
              <p className="font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase sm:col-span-2">Add machine</p>
              <Field id="m-loc" label="Location">
                <select id="m-loc" required value={machine.location_id || (myLocations.length === 1 ? myLocations[0].id : "")}
                  onChange={(e) => setMachine({ ...machine, location_id: e.target.value })} className={selectCls}>
                  {myLocations.length !== 1 && <option value="">Pick a location</option>}
                  {myLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </Field>
              <Field id="m-name" label="Machine name"><Input id="m-name" required value={machine.name} onChange={(e) => setMachine({ ...machine, name: e.target.value })} className={inputCls} placeholder="Lobby cooler" /></Field>
              <Field id="m-place" label="Spot at the location"><Input id="m-place" value={machine.placement} onChange={(e) => setMachine({ ...machine, placement: e.target.value })} className={inputCls} placeholder="Break room, 2nd floor" /></Field>
              <Field id="m-type" label="Type"><Input id="m-type" value={machine.machine_type} onChange={(e) => setMachine({ ...machine, machine_type: e.target.value })} className={inputCls} /></Field>
              <Field id="m-every" label="Service every (days)"><Input id="m-every" type="number" min="1" max="90" value={machine.service_every_days} onChange={(e) => setMachine({ ...machine, service_every_days: e.target.value })} className={inputCls} /></Field>
              <Field id="m-planner" label="VendingPlanner link"><Input id="m-planner" type="url" value={machine.planner_url} onChange={(e) => setMachine({ ...machine, planner_url: e.target.value })} className={inputCls} placeholder="https://…" /></Field>
              <button type="submit" disabled={busy} className={`sm:col-span-2 ${primaryBtn}`}><Plus size={15} /> Add machine</button>
            </form>
          </div>

          {/* Contracts */}
          <div>
            <SectionTitle kicker="Contracts" title="Agreements" />
            <div className="mt-6 border border-white/10">
              {detail.contracts.length === 0 && <p className="px-6 py-8 text-sm text-slate-500">No contract on file.</p>}
              {detail.contracts.map((c) => (
                <div key={c.id} className="border-b border-white/5 px-6 py-5" data-testid={`contract-${c.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <FileText size={14} className="text-vortex-cyan" />
                    <span className="text-sm font-medium text-slate-100">{c.title || "Contract"}</span>
                    <Pill tone={contractTone(c.state)}>{contractLabel(c.state)}</Pill>
                    <span className="ml-auto flex items-center gap-3">
                      {c.document_url && (
                        <a href={c.document_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-vortex-cyan">
                          Signed copy <ExternalLink size={11} />
                        </a>
                      )}
                      <button
                        aria-label="Delete contract"
                        className={iconBtn}
                        onClick={() => window.confirm("Delete this contract record?")
                          && call(() => axios.delete(`${API}/service/contracts/${c.id}`, auth()), "Contract deleted.", "Could not delete contract.")}
                      >
                        <Trash2 size={15} />
                      </button>
                    </span>
                  </div>
                  <p className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400">
                    <span>Signed {fmtDate(c.start_date)}</span>
                    <span>{c.term_months}-month term{c.auto_renew ? ", auto-renews" : ""}</span>
                    <span>Current term ends {fmtDate(c.term_end)}</span>
                    <span>Notice by {fmtDate(c.notice_by)}</span>
                    {c.commission_pct != null && <span>{c.commission_pct}% commission on {c.commission_basis} sales</span>}
                  </p>
                  {c.notes && <p className="mt-2 text-xs text-slate-500">{c.notes}</p>}
                </div>
              ))}
            </div>
            <form
              className={`mt-4 grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 ${card}`}
              onSubmit={async (e) => {
                e.preventDefault();
                const body = {
                  ...contract,
                  term_months: Number(contract.term_months) || 12,
                  notice_days: Number(contract.notice_days) || 0,
                  commission_pct: contract.commission_pct === "" ? null : Number(contract.commission_pct),
                };
                if (await call(() => axios.post(`${API}/service/accounts/${detail.id}/contracts`, body, auth()), "Contract added.", "Could not add contract.")) setContract(emptyContract);
              }}
            >
              <Field id="c-title" label="Title"><Input id="c-title" value={contract.title} onChange={(e) => setContract({ ...contract, title: e.target.value })} className={inputCls} /></Field>
              <Field id="c-start" label="Start date"><Input id="c-start" type="date" required value={contract.start_date} onChange={(e) => setContract({ ...contract, start_date: e.target.value })} className={inputCls} /></Field>
              <Field id="c-term" label="Term (months)"><Input id="c-term" type="number" min="1" max="120" value={contract.term_months} onChange={(e) => setContract({ ...contract, term_months: e.target.value })} className={inputCls} /></Field>
              <Field id="c-notice" label="Notice period (days)"><Input id="c-notice" type="number" min="0" max="365" value={contract.notice_days} onChange={(e) => setContract({ ...contract, notice_days: e.target.value })} className={inputCls} /></Field>
              <Field id="c-comm" label="Commission %"><Input id="c-comm" type="number" min="0" max="100" step="0.5" value={contract.commission_pct} onChange={(e) => setContract({ ...contract, commission_pct: e.target.value })} className={inputCls} placeholder="Leave blank if none" /></Field>
              <Field id="c-basis" label="Commission on">
                <select id="c-basis" value={contract.commission_basis} onChange={(e) => setContract({ ...contract, commission_basis: e.target.value })} className={selectCls}>
                  <option value="gross">Gross sales</option>
                  <option value="net">Net sales</option>
                </select>
              </Field>
              <Field id="c-doc" label="Signed copy link"><Input id="c-doc" type="url" value={contract.document_url} onChange={(e) => setContract({ ...contract, document_url: e.target.value })} className={inputCls} placeholder="https://drive.google.com/…" /></Field>
              <label className="flex items-center gap-3 self-end pb-2 text-sm text-slate-300">
                <input type="checkbox" checked={contract.auto_renew} onChange={(e) => setContract({ ...contract, auto_renew: e.target.checked })} className="h-4 w-4 accent-[#5CB3E2]" />
                Auto-renews
              </label>
              <div className="sm:col-span-2"><Field id="c-notes" label="Notes"><Input id="c-notes" value={contract.notes} onChange={(e) => setContract({ ...contract, notes: e.target.value })} className={inputCls} placeholder="Exclusivity, price changes need approval…" /></Field></div>
              <button type="submit" disabled={busy} className={`sm:col-span-2 ${primaryBtn}`}><Plus size={15} /> Add contract</button>
            </form>
          </div>

          {/* Visits + feedback */}
          <div>
            <SectionTitle kicker="History" title="Recent visits" />
            <div className="mt-6 border border-white/10">
              {detail.visits.length === 0 && <p className="px-6 py-8 text-sm text-slate-500">No visits logged yet.</p>}
              {detail.visits.map((v) => (
                <div key={v.id} className="flex flex-wrap items-start gap-4 border-b border-white/5 px-6 py-4 text-sm">
                  <span className="w-28 shrink-0 font-mono text-xs text-slate-500">{fmtDate(v.visit_date)}</span>
                  <Pill tone="slate">{v.visit_type}</Pill>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-200">{v.machine_name}
                      <span className="text-slate-500">
                        {v.units_stocked != null && ` · ${v.units_stocked} units`}
                        {v.cash_collected != null && ` · ${money(v.cash_collected)}`}
                      </span>
                    </p>
                    {v.notes && <p className="mt-1 text-xs text-slate-400">{v.notes}</p>}
                  </div>
                  <button
                    aria-label="Delete visit"
                    className={iconBtn}
                    onClick={() => window.confirm("Delete this visit?")
                      && call(() => axios.delete(`${API}/service/visits/${v.id}`, auth()), "Visit deleted.", "Could not delete visit.")}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>

            {detail.feedback.length > 0 && (
              <>
                <p className="mt-8 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase">Customer feedback from this site's QR codes</p>
                <div className="mt-3 border border-white/10">
                  {detail.feedback.map((f) => (
                    <div key={f.id} className="border-b border-white/5 px-6 py-4 text-sm">
                      <div className="flex items-center gap-3">
                        <Pill>{f.category}</Pill>
                        <span className="ml-auto font-mono text-[10px] text-slate-500">{new Date(f.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="mt-2 text-slate-300">{f.message}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminService;
